/**
 * Ion Chat Store — Signals + reducers for the AI chat state.
 *
 * Source-of-truth for everything Ion-related the UI renders. All mutations
 * happen through the helper functions exported below; components subscribe via
 * the exported signals.
 *
 * Design contract: see .planning/spark-design-lock.md §4 (state) and §5
 * (event → render mapping). Persistence model: ephemeral UI bits in
 * localStorage, conversation messages re-fetched from the backend on demand
 * (not in F1).
 */

import { computed, effect, signal } from '@preact/signals'
import { safeLocal, safeSession } from '../utils/safeStorage.js'

// ───────────────────────────────────────────────────────────────────────────
// Persistence helpers
// ───────────────────────────────────────────────────────────────────────────

const LS_PREFIX = 'batemo:ion:'

/**
 * Every unprefixed key this store persists. Enumerated (not guessed) so
 * `clearIonPersistedState()` can remove exactly these — keep in sync with the
 * `effect(() => lsSet(...))` calls below when adding a persisted signal.
 */
export const ION_PERSISTED_KEYS = [
  'ionSurface',
  // Legacy names, still purged: pre-IonSurface builds persisted the geometry
  // as chatMode + chatOpen. Harmless to remove keys that no longer exist.
  'chatMode',
  'chatOpen',
  'draftInput',
  'currentConversationId',
  'tokenBudget',
  'quotaWarningDismissed',
]

/**
 * Identity token folded into every persisted key, so one browser profile can
 * never serve another account's Ion residue. Defence in depth BEHIND the
 * explicit clear in `authService.logout()` — if that ever regresses, a
 * different user simply reads different keys.
 *
 * Read fresh on every access: this module is imported before login resolves,
 * so the token flips from 'anon' to 'u<public_id>' mid-session and later writes
 * must land under the real identity.
 *
 * Keyed on `public_id`, NOT `id`. The internal integer id is deliberately absent
 * from GET /users/me — `UserResponse` omits it ("Internal id should never be
 * exposed to frontend", backend/app/api/v1/users/schemas.py) — so reading `.id`
 * here always yielded undefined, every key collapsed back to the shared 'anon'
 * namespace, and this entire defence was dead code. `public_id` is the stable
 * per-account identifier that IS exposed.
 */
export function ionIdentityToken() {
  // safeLocal never throws (null when blocked); the try only guards a corrupt value.
  const raw = safeLocal.get('current_user')
  if (!raw) return 'anon'
  try {
    const publicId = JSON.parse(raw)?.public_id
    return typeof publicId === 'string' && publicId ? `u${publicId}` : 'anon'
  } catch (e) {
    return 'anon'
  }
}

function lsKey(key) {
  return `${LS_PREFIX}${ionIdentityToken()}:${key}`
}

// Storage access goes through safeLocal, which never throws and logs a blocked
// store itself; only a corrupt persisted value is handled here.
function lsGet(key, fallback) {
  const raw = safeLocal.get(lsKey(key))
  if (raw == null) return fallback
  try {
    return JSON.parse(raw)
  } catch (e) {
    console.warn('[ChatStore] ignoring unparseable persisted value:', key, e)
    return fallback
  }
}

function lsSet(key, value) {
  safeLocal.set(lsKey(key), JSON.stringify(value))
}

/**
 * Is this page load an ARRIVAL, or was the user simply never away?
 *
 * The bug this answers: `currentConversationId` is persisted to localStorage,
 * so closing the tab, coming back tomorrow, or logging in again all dropped the
 * user straight back into a stale thread. Every other assistant opens fresh on
 * arrival, and for good reason — yesterday's conversation is context the user
 * has forgotten, and it silently pollutes the next answer.
 *
 * The tempting fix — stop persisting the id — is WRONG, and it is worth naming
 * why, because it looks like the obvious one. Ion's whole premise is that he
 * follows you across the app: Ion full page → Cell Explorer → Compare, docked
 * the whole way, same thread. Drop the persistence and that still works (the
 * SPA never reloads on those routes, so the in-memory signal survives) — but a
 * plain F5 would wipe the thread, and any future full page load would too. The
 * distinction we actually want is not *persisted vs. not*, it is
 * **session vs. navigation**.
 *
 * `sessionStorage` has exactly that lifetime: it dies with the tab, survives
 * reloads within it, and is per-tab so a second tab is its own arrival. Hence:
 *   - marker absent → arrival → fresh thread (the old one is still on the
 *     server and one click away in Recents; nothing is lost)
 *   - marker present → reload or in-app navigation → keep the thread
 *
 * SCOPE, since this used to be the whole rule and no longer is: on `/assistant`
 * the URL now decides (`/assistant` = new chat, `/assistant/<id>` = that one —
 * see AssistantPage). Inferring "continue" from "same tab" was wrong exactly
 * there: a reload of the landing page counted as staying and reopened
 * yesterday's thread. What is left for this marker is the DOCKED case, which has
 * no address of its own — reload the Explorer with Ion docked and the
 * conversation beside it should still be there.
 *
 * Runs ONCE per page load at module init, so it cannot affect client-side
 * navigation — that never re-imports this module.
 */
const ION_SESSION_MARKER = `${LS_PREFIX}sessionActive`

function isFreshArrival() {
  if (safeSession.get(ION_SESSION_MARKER)) return false
  // A failed write means storage is blocked (private mode, hardened settings):
  // a blocked read returns null above, so it lands here too. Fall back to
  // CONTINUING, not to arriving: continuing is exactly today's behaviour, so
  // the worst case is that these users keep the old annoyance rather than
  // gaining a new bug. Treating it as an arrival would instead mean a fresh
  // thread on every full page load for them. (safeStorage logs the failure.)
  if (!safeSession.set(ION_SESSION_MARKER, '1')) return false
  return true
}

function collectIonKeys(predicate) {
  // safeLocal.keys() returns [] (and warns once) when storage is blocked.
  return safeLocal.keys().filter((k) => k && k.startsWith(LS_PREFIX) && predicate(k))
}

/**
 * Remove EVERY persisted Ion key, for every identity (and the legacy un-keyed
 * `batemo:ion:<name>` shape). Called from `authService.logout()` alongside
 * `clearConversation()` so no draft, conversation id or quota mirror survives
 * an account switch in the same browser. Returns the removed keys (test aid).
 */
export function clearIonPersistedState() {
  const removed = new Set(collectIonKeys(() => true))
  // Belt and braces for storages that don't enumerate by index: the enumerated
  // keys under the current identity plus their legacy un-keyed form.
  for (const name of ION_PERSISTED_KEYS) {
    removed.add(lsKey(name))
    removed.add(LS_PREFIX + name)
  }
  const list = [...removed]
  for (const k of list) safeLocal.remove(k)
  return list
}

/**
 * Drop every `batemo:ion:*` key that does NOT belong to the current identity —
 * including the pre-user-keyed `batemo:ion:<name>` shape. Such keys are
 * DELETED, never migrated: a persisted conversation id may belong to another
 * account and adopting it is exactly the bug this guards against (server 500 +
 * "I can see the other account's chat").
 *
 * Runs once at module load, and only once an identity is actually resolved —
 * purging on behalf of 'anon' would delete the signed-in user's own keys during
 * a cold boot before `current_user` is cached.
 */
function purgeForeignIonKeys() {
  const token = ionIdentityToken()
  if (token === 'anon') return
  const mine = `${LS_PREFIX}${token}:`
  for (const k of collectIonKeys((k) => !k.startsWith(mine))) safeLocal.remove(k)
}

// Must run BEFORE the persisted signals below initialise, so they never read a
// foreign / legacy value.
purgeForeignIonKeys()

// ───────────────────────────────────────────────────────────────────────────
// Mode / panel state
// ───────────────────────────────────────────────────────────────────────────

/**
 * Sanitize a persisted `ionSurface` value at hydration (page-load) time. A
 * stale `'full'` from a previous /assistant visit must never cover a
 * DIFFERENT page on reload — IonSurface renders `full` as a modal overlay
 * regardless of the underlying route, and nothing else runs before this
 * module initializes to catch it. Demotes `'full'` → `'rail'` unless the
 * current path IS `/assistant` (or an `/assistant/...` sub-path); any other
 * persisted value (including `'docked'`) passes through unchanged. Exported
 * as a pure function for testing.
 */
export function sanitizeIonSurfaceHydration(persisted, pathname) {
  if (persisted !== 'full') return persisted
  const path = pathname || ''
  if (path === '/assistant' || path.startsWith('/assistant/')) return persisted
  return 'rail'
}

/** 'rail' | 'docked' | 'full' — the single source of truth for Ion's geometry.
 *  rail = slim right launcher strip (replaces the old FAB); docked = right
 *  inset card (old slide-over); full = chat+canvas split (old /assistant). */
export const ionSurface = signal(
  sanitizeIonSurfaceHydration(
    lsGet('ionSurface', 'rail'),
    typeof window !== 'undefined' ? window.location.pathname : '',
  ),
)
effect(() => lsSet('ionSurface', ionSurface.value))

// Backward-compat, read-only. Older code reads these; ionSurface is the truth.
export const chatOpen = computed(() => ionSurface.value !== 'rail')
export const chatMode = computed(() => (ionSurface.value === 'full' ? 'fullpage' : 'slideOver'))

/** Draft text in the input field; persisted so navigating away doesn't lose it. */
export const draftInput = signal(lsGet('draftInput', ''))
effect(() => lsSet('draftInput', draftInput.value))

/**
 * UUID of the active conversation, or null for a fresh one.
 *
 * Adopted from localStorage only when this page load CONTINUES a session (see
 * `isFreshArrival`). On an arrival the persisted id is deliberately dropped —
 * the conversation itself lives on the server and stays reachable through
 * Recents, so a fresh start costs nothing and yesterday's context stops leaking
 * into today's first question.
 *
 * The `draftInput` above is treated the OPPOSITE way on purpose: half-typed text
 * has no other home, so it is still restored on arrival. A thread you can
 * re-open in one click and a sentence that only exists in that box are not the
 * same kind of loss.
 */
const _ionArrival = isFreshArrival()
export const currentConversationId = signal(
  _ionArrival ? null : lsGet('currentConversationId', null),
)
effect(() => lsSet('currentConversationId', currentConversationId.value))

/**
 * True when THIS page load was an arrival (fresh tab, re-login, first open) as
 * opposed to a reload or in-app navigation. Read by the Ion home to decide
 * whether to greet — a greeting on every navigation would be noise.
 */
export const ionArrivedFresh = signal(_ionArrival)

/**
 * True when Ion is tucked into the rail WHILE a conversation exists — the rail
 * then shows an "active chat" indicator so the user can return to it. Derived
 * from `ionSurface` + conversation presence, so (unlike the old signal) both
 * close and minimize land here identically: any time the panel is in the rail
 * with a non-empty conversation, the indicator is on. Persisted via
 * `ionSurface`, so a reload can rehydrate into this state (the rail reopens the
 * persisted conversation).
 */
export const chatMinimized = computed(
  () => ionSurface.value === 'rail' && chatMessages.value.length > 0,
)

/** True after Ion finishes a turn while the chat is minimized — drives the FAB
 *  badge dot. Cleared when the user opens/restores the chat. */
export const chatHasUnseen = signal(false)

/** True while the transient "Ion is ready" toast near the FAB is showing. */
export const chatToastVisible = signal(false)

// ───────────────────────────────────────────────────────────────────────────
// Conversation messages
// ───────────────────────────────────────────────────────────────────────────

/**
 * Array of messages in the active conversation. Each message:
 *   {
 *     id: string,
 *     role: 'user' | 'assistant' | 'system' | 'error',
 *     content: string,                       // text body, may include [[SRC:n]] placeholders
 *     sources: Record<number, SourceData>,   // {0: {cellId, value, unit, label}, ...}
 *     toolCalls: ToolCall[],                 // ordered by start time
 *     kpiGroups: KpiGroup[],                 // inline kpi-group artifacts
 *     figures: Figure[],
 *     status: 'pending' | 'streaming' | 'complete' | 'aborted' | 'error',
 *     createdAt: string                      // ISO-8601
 *   }
 */
export const chatMessages = signal([])

/**
 * Identity token (see `ionIdentityToken`) the messages currently in
 * `chatMessages` were loaded for, or null when the list is empty/unknown.
 * Ephemeral (messages are never persisted) — it exists so a surface can tell
 * "these messages are MINE" from "these are the previous account's residue".
 */
export const messagesIdentity = signal(null)

/** Stamp the in-memory message list as belonging to the current identity. */
export function stampMessagesIdentity() {
  messagesIdentity.value = ionIdentityToken()
}

/**
 * Should the persisted conversation id be (re)fetched from the server?
 *
 * Identity-based, NOT emptiness-based: a non-empty `chatMessages` is no proof
 * that hydration can be skipped. If the account changed in this tab since the
 * messages were loaded, the in-memory thread belongs to the PREVIOUS user and
 * must be replaced by an authoritative user-scoped fetch (which, for a foreign
 * id, 404s and clears it — see ionConversationService.loadConversationIntoStore).
 */
export function needsConversationRehydrate() {
  if (!currentConversationId.peek()) return false
  if (chatMessages.peek().length === 0) return true
  return messagesIdentity.peek() !== ionIdentityToken()
}

// Helper to update a single message in the array immutably.
function updateMessage(messageId, updater) {
  chatMessages.value = chatMessages.value.map((m) =>
    m.id === messageId ? updater(m) : m,
  )
}

function getMessage(messageId) {
  return chatMessages.value.find((m) => m.id === messageId)
}

/**
 * Find a tool call by id across ALL messages of the active conversation.
 *
 * Needed for cross-turn value references: the backend's value-guard lets
 * the AI cite a value it fetched in an EARLIER message (the marker comes
 * back annotated `|via=history`), and tool chaining may reference a prior
 * turn's call too. The renderer's per-message `toolCalls` prop can't see
 * those — this lookup can.
 */
export function findToolCallById(toolCallId) {
  if (!toolCallId) return null
  for (const m of chatMessages.value) {
    const call = (m.toolCalls || []).find((tc) => tc.id === toolCallId)
    if (call) return call
  }
  return null
}

/**
 * Extract a single value (by its `ref`) from ONE tool call's artifact, in
 * the shape DataValueChip needs — or null. Covers every value-bearing
 * artifact: data_value / data_string (root ref) and the multi-value
 * data_values / compare_table (per-entry ref). Phase A: `ref` is the one
 * value identity, unique for the whole conversation.
 */
export function extractValueByRef(call, ref) {
  if (!call || call.isError) return null
  const a =
    call.output && call.output.artifact_type
      ? call.output
      : call.output && call.output.result && call.output.result.artifact_type
        ? call.output.result
        : null
  if (!a) return null
  const t = a.artifact_type
  if ((t === 'data_value' || t === 'data_string') && a.ref === ref) {
    return {
      cell: a.cell,
      metric: a.metric,
      formatted: a.formatted,
      basis: a.basis,
      provenanceTrail: a.provenance_trail,
    }
  }
  if (t === 'data_values') {
    for (const v of a.values || []) {
      if (v.ref === ref && !v.missing) {
        return {
          cell: a.cell || {},
          metric: { name: v.metric_name, label: v.label, unit: v.unit, condition: v.condition },
          formatted: v.formatted,
          provenanceTrail: v.provenance_trail,
        }
      }
    }
  }
  if (t === 'compare_table') {
    for (const row of a.values || []) {
      for (const pm of row.per_metric || []) {
        if (pm.ref === ref && !pm.missing) {
          const cell = (a.cells || [])[row.cell_index] || {}
          return {
            cell,
            metric: { name: pm.metric_name, label: pm.label, unit: pm.unit, condition: pm.condition },
            formatted: pm.formatted,
            provenanceTrail: pm.provenance_trail,
          }
        }
      }
    }
  }
  return null
}

/**
 * Resolve a [[V:N]] citation to its value across the WHOLE conversation —
 * the value may live in an earlier message. This (plus conversation-unique
 * refs from Phase B) is what lets the renderer resolve a citation by ref
 * alone, no owning tool_call_id needed — and is why reopening a chat from
 * history no longer breaks chips.
 */
export function findValueByRef(ref) {
  if (!Number.isInteger(ref)) return null
  for (const m of chatMessages.value) {
    for (const call of m.toolCalls || []) {
      const v = extractValueByRef(call, ref)
      if (v) return v
    }
  }
  return null
}

/** True if any tool call in the conversation is still running — used to
 *  show a placeholder for a [[V:N]] whose value hasn't landed yet. */
export function hasRunningToolCall() {
  for (const m of chatMessages.value) {
    if ((m.toolCalls || []).some((tc) => tc.status === 'running')) return true
  }
  return false
}

// ───────────────────────────────────────────────────────────────────────────
// Streaming state
// ───────────────────────────────────────────────────────────────────────────

/** True while a chat response is being streamed. */
export const chatStreaming = signal(false)

/** The id of the assistant message currently accumulating tokens, or null. */
export const streamingMessageId = signal(null)

/**
 * The active AbortController instance. NOT a serializable value — stored as a
 * raw signal so the stop button can reach it. Use `.peek()` from outside to
 * read without subscribing.
 */
export const abortController = signal(null)

/**
 * One-shot autosend: when set, AssistantPage consumes it ONCE on mount to
 * auto-send the seeded prompt (the guided-entry chips / "Ask Ion"). Ephemeral —
 * deliberately NOT persisted, so a page refresh never re-fires a send.
 * Shape: { text: string, contextHint?: object } | null
 */
export const autoSendDraft = signal(null)

// ───────────────────────────────────────────────────────────────────────────
// Token quota
// ───────────────────────────────────────────────────────────────────────────

/**
 * Dual-bucket quota state matching the backend (`X-Chat-Daily-Used-Pct`,
 * `X-Chat-Weekly-Used-Pct`). Percentages are integers 0–100. Admins receive
 * `isAdmin: true` and effectively `pct: 0` (unlimited). Legacy single-bucket
 * `{used, limit, resetAt}` payloads are accepted by `updateQuota()` and
 * coerced to the dual-bucket shape (treated as the daily bucket).
 */
const DEFAULT_QUOTA = {
  daily: { pct: 0, resetAt: null },
  weekly: { pct: 0, resetAt: null },
  isAdmin: false,
}

function normaliseQuotaShape(raw) {
  if (!raw || typeof raw !== 'object') return DEFAULT_QUOTA
  if (raw.daily && raw.weekly) return raw // already dual-bucket
  // Legacy migration: old shape was {used, limit, resetAt}.
  const pct =
    Number.isFinite(raw.used) && Number.isFinite(raw.limit) && raw.limit > 0
      ? Math.round((raw.used / raw.limit) * 100)
      : 0
  return {
    daily: { pct, resetAt: raw.resetAt || null },
    weekly: { pct: 0, resetAt: null },
    isAdmin: false,
  }
}

export const tokenBudget = signal(normaliseQuotaShape(lsGet('tokenBudget', DEFAULT_QUOTA)))
effect(() => lsSet('tokenBudget', tokenBudget.value))

/** Last model id the backend actually used (from `X-Chat-Model` or `usage` event).
 *  Lets the UI surface a hint when the resilience layer fell back to a different
 *  model than the one the user requested. */
export const lastUsedModel = signal(null)

/**
 * Ion access state. Resolved at bootstrap by `ionAccessService.bootstrapChatAccess()`
 * and updated whenever a chat call returns 403 (kill-switch flips, admin
 * disables the user mid-session). Shape:
 *   {
 *     loading: boolean,            // true until first /chat/usage/me resolves
 *     available: boolean,          // can the user actually call /chat/stream?
 *     isAdmin: boolean,
 *     reason: string | null,       // 'free_tier' | 'no_subscription' | 'inactive'
 *                                  //   | 'disabled_by_admin' | 'kill_switch'
 *                                  //   | 'quota_exceeded' | 'network' | null
 *     allowedModels: string[],     // populated lazily once /chat/models is hit
 *     defaultModel: string | null,
 *   }
 */
export const chatAccess = signal({
  loading: true,
  available: false,
  isAdmin: false,
  reason: null,
  allowedModels: [],
  defaultModel: null,
})

export function setChatAccess(patch) {
  chatAccess.value = { ...chatAccess.value, ...patch }
}

/** Env-level Ion feature flag from GET /chat/usage/me (null = not yet known). */
export const ionFeatureEnabled = signal(null)

/**
 * Whether Ion surfaces must be hidden in this environment / for this user.
 * Hidden if the backend denies access (non-admins on a flag-off env) OR the
 * env feature flag is explicitly off (covers admins, who bypass per-user access).
 * Never hides while access is still loading (avoids a flicker).
 */
export const ionHidden = computed(() => {
  const a = chatAccess.value
  return (!a.loading && !a.available) || ionFeatureEnabled.value === false
})

/**
 * PRO/DEMO "Insights Token" budget ($0.25 USD = 1 token — a pure display unit
 * derived server-side from cost_usd, see backend chat/token_units.py; NOT
 * a raw USD amount). Populated from GET /chat/usage/me
 * (dailyTokensUsed / dailyTokensLimit / dailyWarning). `used`/`limit` are
 * decimal STRINGS (fractional tokens are valid, e.g. "0.4") — parse with
 * Number() to render. limit === null → unlimited (privileged role) → meter
 * hides. NOT persisted — always refreshed from the endpoint.
 *
 * NOTE (2026-07-17): the per-day REQUEST-COUNT meter (`requestBudget`, "X of
 * N questions today") was removed along with the backend request-count cap
 * — DEMO now shows this same token meter (its daily/weekly/monthly USD caps
 * were always the real enforcement; the request count was redundant).
 *
 * NOT the same thing as `tokenBudget` above: that's a legacy name (pre-dates
 * this feature) for the PERCENTAGE-based dual-bucket USD quota. Don't confuse
 * the two — this signal is specifically the Insights Token display unit.
 */
export const insightsTokenBudget = signal({ used: null, limit: null, warning: false })

/**
 * Monthly Insights Token budget (Ion AI limits system, 2026-07) — same
 * token-display convention as `insightsTokenBudget` above, one level up the
 * window hierarchy. Populated from GET /chat/usage/me
 * (monthlyTokensUsed / monthlyTokensLimit). `resetAt` is the literal string
 * "never" for DEMO (one-time total-for-the-account ceiling, never rolls).
 * limit === null → unlimited (privileged role) → meter hides.
 */
export const monthlyTokenBudget = signal({ used: null, limit: null, resetAt: null, warning: false })

/**
 * Weekly Insights Token budget (parity fix, 2026-07-17) — same token-display
 * convention as `insightsTokenBudget`/`monthlyTokenBudget` above, the window
 * in between daily and monthly. Populated from GET /chat/usage/me
 * (weeklyTokensUsed / weeklyTokensLimit). limit === null → unlimited
 * (privileged role) → meter hides.
 */
export const weeklyTokenBudget = signal({ used: null, limit: null, warning: false })

/** Resolved tier/profile grouping from GET /chat/usage/me: 'pro' | 'demo' |
 * 'batemo' | 'none'. Drives which self-service pacing UI (if any) renders. */
export const tierProfile = signal(null)

/** Threshold (80 | 95 | null) above which we last warned the user — prevents
 *  re-spamming toasts on every quota update. */
export const quotaWarningDismissed = signal(lsGet('quotaWarningDismissed', null))
effect(() => lsSet('quotaWarningDismissed', quotaWarningDismissed.value))

// ───────────────────────────────────────────────────────────────────────────
// Artifact canvas (V4 fullpage)
// ───────────────────────────────────────────────────────────────────────────

/**
 * The artifact currently shown in the right pane on /assistant.
 * Auto-pinned by the stream handler when a tool-output with
 * `artifact_type` arrives. Can be manually pinned by the user clicking a
 * preview in the chat flow.
 *
 *   { id, type: 'table' | 'figure' | 'report' | 'kpi-group',
 *     data, messageId, sourceToolCallId? }
 */
export const pinnedArtifact = signal(null)

/**
 * The most recent `application_profile` artifact payload seen this
 * conversation — mirrors the `pinnedArtifact` pattern but is NEVER
 * overwritten by a `ranked_cells_table` pin (which happens to `pinnedArtifact`
 * on every re-rank). ArtifactCanvas uses this to keep a PERSISTENT profile
 * panel (constraints + weight sliders) visible above the Table/Plot/Report
 * tabs for the whole selection flow, instead of the old inline-chat repeat.
 * Set in `endToolCall` and `applyReRankResult`; cleared in
 * `resetSelectionContext` / `clearConversation`.
 */
export const latestApplicationProfile = signal(null)

/**
 * True while a deterministic (non-LLM) re-rank request triggered by dragging
 * a weight slider is in flight (see ApplicationProfileCard's
 * WeightSlidersSection). Lets ArtifactCanvas show the same "Updating…" cue
 * for a slider-driven re-rank as it already does for a streaming chat turn.
 */
export const reRankPending = signal(false)

/** Active tab on the artifact canvas: 'table' | 'plot' | 'report'. */
export const canvasTab = signal('table')

/** True once the user has manually clicked a canvas tab. While false, auto-pin
 *  picks the tab (the live Ragone defaults to 'plot' during a selection flow);
 *  once the user switches, their choice sticks across subsequent re-rank pins. */
export const canvasTabUserOverridden = signal(false)

/** Focus-View toggle for the cell-selection plot: when true, the plot hides the
 *  ruled-out/neutral cells and zooms the axes onto the passing candidates;
 *  when false (default) it shows the full fixed-axis overview of all cells.
 *  Ephemeral — resets with a fresh conversation. */
export const canvasFocusView = signal(false)

/**
 * Cells the user manually pinned from the plot — including ruled-out (greyed)
 * ones, overriding the backend's exclusion (feedback: the customer must keep
 * the freedom to add a cell Ion filtered out). Each entry:
 *   { pointId, manufacturer, name, originalPassed, exclusionReason, pinnedAt }
 * They merge into the ranked shortlist + report (client-side) and render with a
 * pin marker on the plot. Ephemeral, per-conversation (cleared on reset).
 */
export const manualPicks = signal([])

export function addManualPick(pick) {
  if (!pick || !pick.pointId) return
  if (manualPicks.value.some((p) => p.pointId === pick.pointId)) return
  manualPicks.value = [...manualPicks.value, pick]
}

export function removeManualPick(pointId) {
  if (!pointId) return
  manualPicks.value = manualPicks.value.filter((p) => p.pointId !== pointId)
}

/** Toggle a manual pick by identity — click a pinned cell again to unpin it. */
export function toggleManualPick(pick) {
  if (!pick || !pick.pointId) return
  if (manualPicks.value.some((p) => p.pointId === pick.pointId)) {
    removeManualPick(pick.pointId)
  } else {
    addManualPick(pick)
  }
}

export function clearManualPicks() {
  manualPicks.value = []
}

// ───────────────────────────────────────────────────────────────────────────
// Reasoning toggle (per-message, ephemeral — not persisted)
// ───────────────────────────────────────────────────────────────────────────

/** Set of message ids whose reasoning section is currently expanded. */
export const reasoningOpenFor = signal(new Set())

/** Set of tool_call_ids of `ask_user` dialogs that have been answered.
 *  Locks the dialog so the same question can't be re-answered after
 *  the user clicked an option (or hit Send on the freeform field).
 *  Ephemeral — not persisted; if a user reloads mid-conversation the
 *  dialog re-opens, which is the right UX (server has not yet
 *  received the answer either). */
export const askUserAnswered = signal(new Set())

export function markAskUserAnswered(toolCallId) {
  if (!toolCallId) return
  if (askUserAnswered.value.has(toolCallId)) return
  const next = new Set(askUserAnswered.value)
  next.add(toolCallId)
  askUserAnswered.value = next
}

export function isAskUserAnswered(toolCallId) {
  return askUserAnswered.value.has(toolCallId)
}

// ───────────────────────────────────────────────────────────────────────────
// Sales-Selection workflow context
// ───────────────────────────────────────────────────────────────────────────

/**
 * Frontend mirror of the server-side `ion_selection_context` row. The
 * authoritative state lives in the backend (see F1-PLAN §5.3); this signal
 * is updated by the stream dispatcher whenever a tool-output with
 * `artifact_type: 'application_profile' | 'ranked_cells_table'` arrives,
 * and consumed by `WorkflowProgress`, `ApplicationProfileCard`, and the
 * Open-in-Compare / Open-in-Benchmarking handoff CTAs.
 *
 * Ephemeral, not persisted — when the user reloads, the backend re-injects
 * the [SELECTION CONTEXT] block in the next system prompt anyway.
 *
 * Shape:
 *   {
 *     branch: 'power_tool' | 'ebike' | 'oem_automotive' | 'bess' | 'drone' | null,
 *     branchLabel: string | null,                  // e.g. 'Power Tool'
 *     stage: 'identify' | 'refine' | 'shortlist' | 'trade_offs' | null,
 *     hardConstraints: Record<string, {op: string, value: number, unit?: string}>,
 *     scoringWeights: Record<string, number>,      // sums to ~1.0
 *     candidateSet: string[],                      // productIds
 *     excludedCells: string[],
 *     answeredQuestions: string[],                 // question slugs
 *     packTopology: {s: number, p: number} | null,
 *     procurementRegion: 'EU' | 'US' | 'CN' | null,
 *     disclaimers: string[],
 *     constraintProvenance: Record<string, 'suggested'|'user_confirmed'>,   // v2
 *     weightingSlugs: string[],                                            // v2
 *     mustAskSlugs: string[],                                              // v2
 *     defaults: Record<string, any>,                                       // v2 (pack-math etc., not rendered)
 *     systemRequirements: Record<string, any> | null,                      // A2
 *     weightProvenance: Record<string, 'flow'|'ai_suggested'|'user_set'>,  // A5
 *   }
 */
const EMPTY_SELECTION_CONTEXT = {
  branch: null,
  branchLabel: null,
  stage: null,
  // Active flow phase (reduction | requirement | weighting | null-when-done) —
  // drives the phase breadcrumb, independent of the coarse `stage`.
  activePhase: null,
  hardConstraints: {},
  scoringWeights: {},
  candidateSet: [],
  excludedCells: [],
  answeredQuestions: [],
  packTopology: null,
  procurementRegion: null,
  disclaimers: [],
  constraintProvenance: {},
  weightingSlugs: [],
  mustAskSlugs: [],
  defaults: {},
  systemRequirements: null,
  weightProvenance: {},
}

export const selectionContext = signal(EMPTY_SELECTION_CONTEXT)

/** Shallow-merge a patch into the current selection context. Caller is
 *  responsible for passing nested objects fully-formed if it wants to
 *  replace them (we don't deep-merge). */
export function setSelectionContext(patch) {
  if (!patch || typeof patch !== 'object') return
  selectionContext.value = { ...selectionContext.value, ...patch }
}

/** Reset to empty — call when starting a fresh conversation or when the
 *  user explicitly aborts the workflow ('Skip to Benchmarking'). */
export function resetSelectionContext() {
  selectionContext.value = EMPTY_SELECTION_CONTEXT
  latestApplicationProfile.value = null
  reRankPending.value = false
}

// ───────────────────────────────────────────────────────────────────────────
// Computed signals
// ───────────────────────────────────────────────────────────────────────────

/**
 * Worst-case percentage across the two buckets, normalised to 0..1.
 * Admins (isAdmin: true) always return 0 — they have no enforceable quota.
 */
export const quotaPercent = computed(() => {
  const q = tokenBudget.value
  if (q.isAdmin) return 0
  const dailyPct = q.daily?.pct || 0
  const weeklyPct = q.weekly?.pct || 0
  return Math.min(Math.max(dailyPct, weeklyPct) / 100, 1)
})

export const canSend = computed(
  () =>
    !chatStreaming.value &&
    draftInput.value.trim().length > 0 &&
    quotaPercent.value < 1,
)

export const lastMessage = computed(() => {
  const msgs = chatMessages.value
  return msgs.length > 0 ? msgs[msgs.length - 1] : null
})

/** True once a branch has been identified (i.e. `apply_application_profile`
 *  has resolved). Drives the WorkflowProgress breadcrumb visibility and the
 *  Open-in-Compare / Open-in-Benchmarking CTAs in the canvas footer. */
export const salesModeActive = computed(() => selectionContext.value.branch !== null)

/** Stages during which a live ranked plot is the PRIMARY canvas artifact. */
export const FLOW_ACTIVE_STAGES = new Set(['refine', 'shortlist', 'trade_offs'])

/** True when a selection flow is live with a ranked plot pinned. Used to keep
 *  that plot sticky: a transient value chip arriving mid-flow must NOT evict it
 *  (value chips already render inline in the chat). See endToolCall's pin guard. */
export const selectionFlowActive = computed(
  () =>
    pinnedArtifact.value?.type === 'ranked_cells_table' &&
    FLOW_ACTIVE_STAGES.has(selectionContext.value.stage),
)

/**
 * Should the artifact canvas (the right-hand column) exist at all? (B3)
 *
 * Only during a guided cell selection. Everywhere else the canvas showed a copy
 * of something the chat already contained — the review's "the plot is rendered
 * twice" — plus a tab strip whose Plot/Table/Report are the three stages of a
 * flow that was not running. Everything that used to pin now has a home in the
 * message itself: plots and compare tables render inline, grouped aggregations
 * since B1, reports as a card since B2, values as chips, KPI groups via markers.
 * Nothing is lost by removing the column; a duplicate is.
 *
 * TWO conditions OR'd rather than just `salesModeActive`, because of the case
 * that would fail SILENTLY: a flow conversation re-opened tomorrow only gets its
 * branch back because `pinLatestArtifactFromMessages` replays the
 * `application_profile` out of the history. Were that profile ever missing —
 * truncated history, a flow that started some other way — the branch would stay
 * null, the column would vanish, and the user would lose the Requirements rail
 * and the Open-in-Compare exit from a shortlist plainly on their screen. Both
 * conditions independently mean "a guided selection is here", so either one
 * keeps the column.
 *
 * A pinned `ranked_cells_table` was a THIRD condition until 2026-09-03 and is
 * deliberately gone. The guided flow was switched off on 2026-08-31, but
 * `rank_cells_for_application` stayed on (its own `ion_playbook_ranking_enabled`
 * flag) — so an ordinary, flow-less ranking kept opening the split view and
 * wrapping the answer in the chrome of a workflow that is not running
 * (Requirements rail, Plot/Table/Report tabs, handoff CTAs — all of which read
 * null state). The ranking now renders INLINE in the transcript
 * (RankedCellsInline, wired in MarkdownRenderer's ToolMarkerSlot), which is
 * where every other tool output already lives. The artifact still PINS: the
 * canvas keeps its `ranked_cells_table` renderers, so switching the guided flow
 * back on restores the canvas view with no further change here.
 */
export const canvasSurfaceActive = computed(
  () => salesModeActive.value || !!latestApplicationProfile.value,
)

// ───────────────────────────────────────────────────────────────────────────
// Mutators — open/close, mode
// ───────────────────────────────────────────────────────────────────────────

// Auto-dismiss timer id for the "Ion is ready" toast (module-scoped so it
// survives component re-renders and is cleared on open/dismiss).
let _toastTimer = null
const TOAST_DISMISS_MS = 5000

/** Clear the completion badge + toast (and its timer). */
function clearUnseen() {
  chatHasUnseen.value = false
  chatToastVisible.value = false
  if (_toastTimer != null) {
    clearTimeout(_toastTimer)
    _toastTimer = null
  }
}

/** Set the geometry directly — the primitive all other mutators build on. */
export function setIonSurface(surface) {
  ionSurface.value = surface
}

export function openChat() {
  ionSurface.value = 'docked'
  clearUnseen()
}

/**
 * Close the chat back into the rail. Non-destructive — the conversation stays
 * recoverable. Clears the unseen badge/toast (`clearUnseen`). NOTE: in the
 * one-panel model this no longer clears the rail's "active chat" indicator —
 * `chatMinimized` derives from conversation presence, so while a conversation
 * exists the rail keeps its active indicator whether the user closed or
 * minimized (the two only differ in whether the badge was cleared).
 */
export function closeChat() {
  ionSurface.value = 'rail'
  clearUnseen()
}

/**
 * Minimize the chat into the rail. The reverse-grow animation plays (CSS,
 * driven by removing `--open`), the conversation is KEPT, and the rail
 * shows an active indicator. Clicking it restores the same chat.
 */
export function minimizeChat() {
  ionSurface.value = 'rail'
}

/** Restore a minimized chat — reopens the SAME conversation with the grow
 *  animation and clears the indicator/badge/toast. */
export function restoreChat() {
  ionSurface.value = 'docked'
  clearUnseen()
}

/** Flag a completed turn while minimized: badge + auto-dismissing toast. */
export function markReady() {
  chatHasUnseen.value = true
  chatToastVisible.value = true
  if (_toastTimer != null) clearTimeout(_toastTimer)
  if (typeof setTimeout === 'function') {
    _toastTimer = setTimeout(() => {
      chatToastVisible.value = false
      _toastTimer = null
    }, TOAST_DISMISS_MS)
  }
}

/** Hide the toast immediately (keeps the FAB badge until the chat is opened). */
export function dismissToast() {
  chatToastVisible.value = false
  if (_toastTimer != null) {
    clearTimeout(_toastTimer)
    _toastTimer = null
  }
}

export function expandToFullpage() {
  ionSurface.value = 'full'
  clearUnseen()
}

/** Leave fullpage back to the docked inset (e.g. navigating away from
 *  /assistant while keeping the conversation reachable). */
export function collapseFromFull() {
  ionSurface.value = 'docked'
}

// When Ion finishes a turn (streaming true → false) while the chat is
// MINIMIZED, surface the completion (badge + toast). Reading chatMinimized via
// peek() so this effect reacts only to the streaming transition.
let _prevStreaming = chatStreaming.peek()
effect(() => {
  const streaming = chatStreaming.value
  if (_prevStreaming && !streaming && chatMinimized.peek()) {
    markReady()
  }
  _prevStreaming = streaming
})

// If Ion access is lost while minimized (kill-switch, admin disable), drop
// the active-chat affordances so we never show a chat the user can't reopen.
// Gate on ionSurface directly (rather than chatMinimized, which now also
// requires a non-empty conversation) since the badge/toast this clears are
// rail-only affordances regardless of whether a conversation is loaded.
effect(() => {
  const access = chatAccess.value
  if (!access.loading && !access.available && ionSurface.peek() === 'rail') {
    clearUnseen()
  }
})

// ───────────────────────────────────────────────────────────────────────────
// Admin feedback (in-chat capture → "Export for Claude Code")
// ───────────────────────────────────────────────────────────────────────────

/**
 * Drives the admin-only FeedbackModal. null = closed. When set:
 *   { scope: 'message' | 'conversation', targetTurnIndex?: number }
 * `targetTurnIndex` is the 0-based ordinal among ASSISTANT messages (only
 * for scope='message'). Not persisted — feedback is left in-session.
 */
export const feedbackTarget = signal(null)

export function openFeedback(target) {
  feedbackTarget.value = target || null
}

export function closeFeedback() {
  feedbackTarget.value = null
}

// ───────────────────────────────────────────────────────────────────────────
// Mutators — message lifecycle
// ───────────────────────────────────────────────────────────────────────────

function newId() {
  return (
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : 'm-' + Math.random().toString(36).slice(2, 11) + Date.now().toString(36)
  )
}

export function appendUserMessage(text, meta = null) {
  const userMsg = {
    id: newId(),
    role: 'user',
    content: text,
    sources: {},
    toolCalls: [],
    kpiGroups: [],
    figures: [],
    status: 'complete',
    createdAt: new Date().toISOString(),
    // Optional render hints (e.g. { askUserAnswer: true } so the answer isn't
    // shown as a duplicate user bubble — the ask_user card already shows it).
    ...(meta ? { meta } : {}),
  }
  chatMessages.value = [...chatMessages.value, userMsg]
  // These messages belong to whoever is logged in right now.
  stampMessagesIdentity()
  return userMsg
}

export function startAssistantMessage() {
  const assistantMsg = {
    id: newId(),
    role: 'assistant',
    content: '',
    sources: {},
    toolCalls: [],
    kpiGroups: [],
    figures: [],
    status: 'streaming',
    createdAt: new Date().toISOString(),
  }
  chatMessages.value = [...chatMessages.value, assistantMsg]
  streamingMessageId.value = assistantMsg.id
  chatStreaming.value = true
  return assistantMsg
}

/**
 * Append a text-delta chunk. Optimised for repeated calls during streaming —
 * the caller (useChatStream) batches deltas across animation frames.
 */
export function appendDelta(messageId, delta) {
  updateMessage(messageId, (m) => ({ ...m, content: m.content + delta }))
}

/**
 * Insert a source reference at the current end of the assistant text.
 * Inserts a `[[SRC:n]]` placeholder which MarkdownRenderer replaces later
 * with the actual <SourceHighlight> component.
 */
export function attachSource(messageId, sourceData) {
  updateMessage(messageId, (m) => {
    const idx = Object.keys(m.sources).length
    return {
      ...m,
      content: m.content + `[[SRC:${idx}]]`,
      sources: { ...m.sources, [idx]: sourceData },
    }
  })
}

export function startToolCall(messageId, { toolCallId, toolName, input, status }) {
  // Inject a `[[TOOL:id]]` placeholder at the current end of the assistant
  // text. MarkdownRenderer replaces it with the inline tool affordance
  // (verified-data chip for data_value, plot caption for plotly_chart, or
  // a "running…" pill for non-renderable tools). This is the same pattern
  // used for [[SRC:n]] and [[KPI:id]] — see attachSource above.
  // Anchoring tool cards to where they were emitted (as opposed to a
  // bottom-of-message stack) matches Claude.ai's UX and lets users follow
  // the assistant's reasoning chronologically.
  updateMessage(messageId, (m) => ({
    ...m,
    content: m.content + `[[TOOL:${toolCallId}]]`,
    toolCalls: [
      ...m.toolCalls,
      {
        id: toolCallId,
        name: toolName,
        input: input || {},
        output: null,
        status: 'running',
        // Localisation descriptor {key, params} for the human status line.
        statusDescriptor: status || null,
        startedAt: Date.now(),
        endedAt: null,
        isError: false,
      },
    ],
  }))
}

export function updateToolCallInput(messageId, toolCallId, input) {
  updateMessage(messageId, (m) => ({
    ...m,
    toolCalls: m.toolCalls.map((t) =>
      t.id === toolCallId ? { ...t, input } : t,
    ),
  }))
}

export function endToolCall(messageId, toolCallId, { output, isError = false }) {
  updateMessage(messageId, (m) => ({
    ...m,
    toolCalls: m.toolCalls.map((t) =>
      t.id === toolCallId
        ? { ...t, output, isError, status: 'done', endedAt: Date.now() }
        : t,
    ),
  }))

  // Auto-pin if the tool output is an artifact (per design-lock §5).
  // Two valid shapes:
  //   - { artifact_type, ...payload }                — flat (BA mock + early F1)
  //   - { result: { artifact_type, ...payload } }    — backend executor
  //     wraps every successful tool return as {result: ...} via _success.
  // We unwrap once and pass `data: payload` so PlotArtifact / TableArtifact
  // see the artifact fields directly regardless of which envelope shape
  // the wire event carried.
  if (!isError && output) {
    const payload =
      output.artifact_type
        ? output
        : output.result && output.result.artifact_type
          ? output.result
          : null
    // Only pin canvas-renderable artifacts. data_series is an intermediate
    // handle; ask_user / nav_link render inline in the chat — pinning them
    // broke the canvas (see NON_CANVAS_ARTIFACT_TYPES).
    if (payload && !NON_CANVAS_ARTIFACT_TYPES.has(payload.artifact_type)) {
      // Sticky live plot: a value chip arriving mid-flow must NOT evict the
      // pinned ranked_cells_table (the chip already shows inline in the chat).
      // Outside a flow, a one-off value chip is still allowed to pin.
      const isValueChip = VALUE_CHIP_ARTIFACT_TYPES.has(payload.artifact_type)
      // B2: a report is a DELIVERABLE, not something to understand in passing —
      // the industry split is that a thing you copy, export or iterate on belongs
      // in a panel, while a thing you look at to answer the question in front of
      // you belongs in the message. A panel that opens itself takes the screen
      // for something the user has not decided to read yet, so outside the
      // guided flow a report waits behind a card in the chat and pins only when
      // the user clicks "Open report" (see ReportOpenCard).
      //
      // INSIDE the flow it still auto-pins: there the report is the flow's final
      // stage, the canvas is already open, and the user got there by walking the
      // funnel to it — the click would be ceremony.
      const isUnrequestedReport =
        payload.artifact_type === 'report' && !salesModeActive.value
      if (!(isValueChip && selectionFlowActive.value) && !isUnrequestedReport) {
        pinArtifact({
          id: newId(),
          type: payload.artifact_type,
          data: payload,
          messageId,
          sourceToolCallId: toolCallId,
        })
      }

      // Mirror the profile into its own signal — unlike pinnedArtifact, this
      // survives a later ranked_cells_table pin, so the canvas can keep the
      // persistent profile panel visible for the whole flow.
      if (payload.artifact_type === 'application_profile') {
        latestApplicationProfile.value = payload
      }

      // Sales-Selection F1 (see F1-PLAN §6.5): mirror server-side
      // selection_context locally so the WorkflowProgress breadcrumb,
      // ApplicationProfileCard, and CanvasHandoffCTAs all see the same
      // state. Stage transitions are heuristic — backend can override
      // explicitly later if needed. Runs UNCONDITIONALLY — a re-rank must
      // still update candidateSet/stage even though a value chip wouldn't.
      applySelectionContextFromArtifact(payload)
    }
  }
}

/**
 * Inspect a tool-output artifact and merge any Sales-Selection state into
 * the selectionContext signal. Pure function over `setSelectionContext` —
 * unrelated artifact types are ignored. Exported for tests.
 *
 * Heuristic stage progression:
 *   - 'application_profile'   → stage='refine'        (branch identified;
 *                                                      now collecting answers)
 *   - 'ranked_cells_table'    → stage='trade_offs'    (we have a shortlist;
 *                                                      time to hand off)
 */
export function applySelectionContextFromArtifact(payload) {
  if (!payload || typeof payload !== 'object') return

  // Forward-compat: if the backend ever streams the authoritative
  // selection_context blob alongside an artifact (snake_case, per
  // ion/sales-selection-contract.md §4), trust it over the local
  // heuristic below — the server owns the workflow stage.
  const sc = payload.selection_context
  if (sc && typeof sc === 'object') {
    setSelectionContext({
      branch: sc.application_branch ?? selectionContext.value.branch,
      branchLabel: sc.branch_label ?? selectionContext.value.branchLabel,
      stage: sc.workflow_stage ?? selectionContext.value.stage,
      ...(sc.active_phase !== undefined ? { activePhase: sc.active_phase } : {}),
      ...(sc.hard_constraints ? { hardConstraints: sc.hard_constraints } : {}),
      ...(sc.scoring_weights ? { scoringWeights: sc.scoring_weights } : {}),
      ...(Array.isArray(sc.candidate_set) ? { candidateSet: sc.candidate_set } : {}),
      ...(Array.isArray(sc.excluded_cells) ? { excludedCells: sc.excluded_cells } : {}),
      ...(Array.isArray(sc.answered_questions) ? { answeredQuestions: sc.answered_questions } : {}),
      ...(sc.pack_topology !== undefined ? { packTopology: sc.pack_topology } : {}),
      ...(sc.procurement_region !== undefined ? { procurementRegion: sc.procurement_region } : {}),
      ...(sc.constraint_provenance ? { constraintProvenance: sc.constraint_provenance } : {}),
      ...(Array.isArray(sc.weighting_slugs) ? { weightingSlugs: sc.weighting_slugs } : {}),
      ...(Array.isArray(sc.must_ask_slugs) ? { mustAskSlugs: sc.must_ask_slugs } : {}),
      ...(sc.defaults ? { defaults: sc.defaults } : {}),
      ...(sc.system_requirements !== undefined
        ? { systemRequirements: sc.system_requirements }
        : {}),
      ...(sc.weight_provenance ? { weightProvenance: sc.weight_provenance } : {}),
    })
    return
  }

  if (payload.artifact_type === 'application_profile') {
    setSelectionContext({
      branch: payload.branch_slug || selectionContext.value.branch,
      branchLabel: payload.branch_label || selectionContext.value.branchLabel,
      stage: 'refine',
      ...(payload.active_phase !== undefined ? { activePhase: payload.active_phase } : {}),
      hardConstraints: payload.hard_constraints || {},
      scoringWeights: payload.scoring_weights || {},
      disclaimers: Array.isArray(payload.disclaimers) ? payload.disclaimers : [],
      ...(payload.constraint_provenance
        ? { constraintProvenance: payload.constraint_provenance }
        : {}),
      ...(payload.defaults ? { defaults: payload.defaults } : {}),
    })
    return
  }

  if (payload.artifact_type === 'ranked_cells_table') {
    const rows = Array.isArray(payload.rows) ? payload.rows : []
    const candidateSet = rows
      .filter((r) => r && r.manufacturer && r.name)
      .map((r) => `${r.manufacturer}::${r.name}`)
    // Don't force a stage here — a PROGRESSIVE re-rank during `refine` must not
    // jump the breadcrumb to trade_offs. Stage transitions come from the
    // server's authoritative selection_context blob (handled above).
    setSelectionContext({ candidateSet })
  }
}

/**
 * Apply the JSON result of a `POST .../re-rank` call (see reRankService.js):
 * a `ranked_cells_table` artifact payload, optionally carrying a
 * `selection_context` blob (same shape a streamed tool-output can carry — see
 * the `sc` branch of applySelectionContextFromArtifact above). Not tied to a
 * chat message (the re-rank endpoint never runs an LLM turn), so it pins the
 * artifact with no messageId/sourceToolCallId, mirroring endToolCall's
 * unwrap-then-pin-then-sync-context sequence for a non-streamed result.
 */
export function applyReRankResult(payload) {
  if (!payload || typeof payload !== 'object') return
  const artifact =
    payload.artifact_type
      ? payload
      : payload.result && payload.result.artifact_type
        ? payload.result
        : null
  if (!artifact) return
  // Forward-compat: if a re-rank response ever carries a refreshed profile
  // (not currently the case — re-rank returns ranked_cells_table — but the
  // contract leaves room for it), keep the persistent panel in sync too.
  if (artifact.artifact_type === 'application_profile') {
    latestApplicationProfile.value = artifact
  }
  pinArtifact({
    id: newId(),
    type: artifact.artifact_type,
    data: artifact,
    messageId: null,
    sourceToolCallId: null,
  })
  applySelectionContextFromArtifact(artifact)
}

export function addFigure(messageId, figureData) {
  updateMessage(messageId, (m) => ({
    ...m,
    figures: [...m.figures, figureData],
  }))
  pinArtifact({
    id: newId(),
    type: 'figure',
    data: figureData,
    messageId,
  })
}

export function addKpiGroup(messageId, kpiGroupData) {
  updateMessage(messageId, (m) => {
    // KPI-group is also rendered inline in the doc flow; we insert a
    // placeholder in the content so MarkdownRenderer can position it.
    const placeholder = `[[KPI:${kpiGroupData.id}]]`
    return {
      ...m,
      content: m.content + placeholder,
      kpiGroups: [...m.kpiGroups, kpiGroupData],
    }
  })
}

export function markDone(messageId, { reason = 'stop' } = {}) {
  updateMessage(messageId, (m) => ({
    ...m,
    status: reason === 'error' ? 'error' : 'complete',
    finishReason: reason,
  }))
  if (streamingMessageId.value === messageId) {
    streamingMessageId.value = null
    chatStreaming.value = false
  }
}

export function markError(messageId, { code, message, recoverable = false }) {
  updateMessage(messageId, (m) => ({
    ...m,
    status: 'error',
    error: { code, message, recoverable },
  }))
  if (streamingMessageId.value === messageId) {
    streamingMessageId.value = null
    chatStreaming.value = false
  }
}

export function markAborted(messageId) {
  updateMessage(messageId, (m) => ({ ...m, status: 'aborted' }))
  if (streamingMessageId.value === messageId) {
    streamingMessageId.value = null
    chatStreaming.value = false
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Mutators — quota, artifact, reasoning
// ───────────────────────────────────────────────────────────────────────────

/**
 * Update the quota state. Accepts two shapes:
 *
 *   1) New dual-bucket: `{daily: {pct, resetAt}, weekly: {pct, resetAt}, isAdmin?}`
 *      — preferred. Missing fields fall back to current values.
 *   2) Legacy single-bucket: `{used, limit, resetAt}` — kept for the
 *      transitional period where some surfaces still produce that shape (and
 *      for the in-stream `quota` event whose schema is still single-bucket).
 *      Mapped onto the daily bucket as a percentage.
 */
export function updateQuota(payload) {
  if (!payload || typeof payload !== 'object') return
  const current = tokenBudget.value

  if (payload.daily || payload.weekly || typeof payload.isAdmin === 'boolean') {
    tokenBudget.value = {
      daily: payload.daily
        ? {
            pct: Number.isFinite(payload.daily.pct)
              ? payload.daily.pct
              : current.daily.pct,
            resetAt: payload.daily.resetAt || current.daily.resetAt,
          }
        : current.daily,
      weekly: payload.weekly
        ? {
            pct: Number.isFinite(payload.weekly.pct)
              ? payload.weekly.pct
              : current.weekly.pct,
            resetAt: payload.weekly.resetAt || current.weekly.resetAt,
          }
        : current.weekly,
      isAdmin:
        typeof payload.isAdmin === 'boolean' ? payload.isAdmin : current.isAdmin,
    }
    return
  }

  // Legacy single-bucket — coerce into daily bucket so downstream computeds
  // continue to work without forcing every callsite to migrate at once.
  const { used, limit, resetAt } = payload
  const limitNum = Number.isFinite(limit) ? limit : null
  const usedNum = Number.isFinite(used) ? used : null
  const isUnlimited = limitNum === -1
  const pct =
    isUnlimited || limitNum == null || limitNum <= 0 || usedNum == null
      ? current.daily.pct
      : Math.round((usedNum / limitNum) * 100)
  tokenBudget.value = {
    ...current,
    daily: {
      pct,
      resetAt: resetAt || current.daily.resetAt,
    },
    isAdmin: isUnlimited ? true : current.isAdmin,
  }
}

/**
 * Apply a live per-window token snapshot from the turn-end `quota` stream
 * event (backend `chat/events.py::Quota`, populated 2026-07-17) to the SAME
 * meter signals the initial `GET /chat/usage/me` bootstrap populates — so the
 * usage meter's daily/weekly/monthly bars update the instant a turn finishes,
 * with no page refresh.
 *
 * The event's `daily`/`weekly`/`monthly` are `{ used, limit, warning, blocked,
 * reset_at }` with `used`/`limit` as decimal token STRINGS ($0.25 = 1 token;
 * `limit === null` = unlimited → the meter hides that window) — the exact
 * shape `insightsTokenBudget` / `weeklyTokenBudget` / `monthlyTokenBudget`
 * already store. TOKENS ONLY — the backend never sends USD here (DEC-6). Each
 * window is optional (an older backend omits them), so we only overwrite a
 * signal when its window is present.
 */
export function applyLiveQuotaSnapshot(event) {
  if (!event || typeof event !== 'object') return
  if (event.daily && typeof event.daily === 'object') {
    insightsTokenBudget.value = {
      used: typeof event.daily.used === 'string' ? event.daily.used : null,
      limit: typeof event.daily.limit === 'string' ? event.daily.limit : null,
      warning: !!event.daily.warning,
    }
  }
  if (event.weekly && typeof event.weekly === 'object') {
    weeklyTokenBudget.value = {
      used: typeof event.weekly.used === 'string' ? event.weekly.used : null,
      limit: typeof event.weekly.limit === 'string' ? event.weekly.limit : null,
      warning: !!event.weekly.warning,
    }
  }
  if (event.monthly && typeof event.monthly === 'object') {
    monthlyTokenBudget.value = {
      used: typeof event.monthly.used === 'string' ? event.monthly.used : null,
      limit: typeof event.monthly.limit === 'string' ? event.monthly.limit : null,
      resetAt: event.monthly.reset_at || null,
      warning: !!event.monthly.warning,
    }
  }
  if (typeof event.tier_profile === 'string') {
    tierProfile.value = event.tier_profile
  }
}

/**
 * Persist a conversation id received from the backend (typically the
 * `X-Conversation-Id` response header). Only writes if the value differs to
 * avoid noisy effect/localStorage churn on every roundtrip.
 */
export function setCurrentConversationId(id) {
  if (!id || typeof id !== 'string') return
  if (currentConversationId.value === id) return
  currentConversationId.value = id
}

/** Record the model the backend actually executed (from `X-Chat-Model` header
 *  or `usage.model_id` event). Surface it in the UI via `lastUsedModel`. */
export function setLastUsedModel(modelId) {
  if (typeof modelId === 'string' && modelId.length > 0) {
    lastUsedModel.value = modelId
  }
}

/**
 * Artifact types that must NEVER be pinned to the canvas — they render
 * inline in the chat (ask_user dialog, nav_link button) or are intermediate
 * handles (data_series). Pinning them sent non-table payloads into
 * TableArtifact, which dumped raw JSON as "Unexpected table shape" and, worse,
 * OVERWROTE the real ranked_cells_table pin so the Plot/Report tabs went blank.
 */
const NON_CANVAS_ARTIFACT_TYPES = new Set([
  'data_series', 'ask_user', 'nav_link', 'web_consent_request', 'web_results',
  // `explorer_command` is an ACTION (set_filters / set_view / set_context)
  // carried out by the command layer — there is nothing to look at. Pinning it
  // put "This result can't be shown as a table" in the canvas and, per the note
  // above, could evict a real table or plot pinned earlier in the same turn.
  // This affects EVERY live turn in which Ion steers the Explorer, not just a
  // gallery replay; it surfaced while watching the steer-explorer recording
  // play back, where steering is all the turn does.
  'explorer_command',
  // `compare_table` (compare_cells_table) already renders inline in the chat as
  // a real table — CompareTable.jsx, via MarkdownRenderer. The canvas has no
  // branch for it, so pinning it showed the fallback message AND, because it
  // arrives last in a report-style turn, it evicted the three plotly_charts
  // that turn had produced: the exact "OVERWROTE the real pin so the Plot tab
  // went blank" failure this set exists to prevent. Live bug, not replay-only.
  'compare_table',
  // `data_string` (get_cell_metric) is a single value rendered inline. Neither
  // ArtifactCanvas nor PlotArtifact has a branch for it — only the SINGULAR
  // `data_value` — so pinning it can only ever produce the fallback message.
  'data_string',
  // `data_values` (get_cell_metrics, estimate_pack) — the PLURAL. Same gap:
  // ArtifactCanvas routes only `data_value` to PlotArtifact, and PlotArtifact
  // keys on `data_value` too, so the plural falls through to TableArtifact and
  // shows the fallback. It is still listed in VALUE_CHIP_ARTIFACT_TYPES below,
  // whose rule (a one-off chip MAY pin outside a selection flow) is now moot for
  // it — this set wins. Kept in both lists deliberately: when PlotArtifact
  // learns the plural shape, deleting this one line restores the intended
  // behaviour without having to reconstruct why it was ever allowed.
  'data_values',
])

/*
 * THE RULE this set encodes, so the next addition doesn't have to rediscover
 * it: a payload may only pin if the canvas can actually render it. Today that
 * means one of ArtifactCanvas's explicit branches (`figure`, `plotly_chart`,
 * `data_value`, `ranked_cells_table`, `application_profile`, `report`) or a
 * `{columns, rows}` shape TableArtifact can lay out. Anything else — anything
 * MarkdownRenderer draws inline, any intermediate handle, any action — belongs
 * in the set above. It is a denylist rather than an allowlist only because
 * TableArtifact's default branch legitimately accepts any table-shaped payload,
 * which an allowlist would have to enumerate exhaustively.
 *
 * `data_values` was the third instance of the same mismatch and is now excluded
 * too — see its entry above. It sat in VALUE_CHIP_ARTIFACT_TYPES below, which by
 * an explicit earlier decision lets a one-off value chip pin outside a selection
 * flow "so the user can keep a looked-up value visible next to the chat". That
 * intent is right, but the canvas renders only the SINGULAR `data_value`
 * (ArtifactCanvas routes it to PlotArtifact; PlotArtifact keys on
 * `artifact_type === 'data_value'`), so for the plural the allowance could only
 * ever produce "This result can't be shown as a table". A broken canvas is
 * strictly worse than no pin, so the exclusion stands until the renderer exists.
 * THE REAL FIX is to teach PlotArtifact the plural shape and then remove
 * `data_values` from the exclusion — a feature, not a bug fix.
 */

/**
 * Value chips (a single verified number / a small bundle). They render INLINE
 * in the chat already, so DURING an active selection flow they must NOT pin to
 * the canvas — that would evict the live ranked plot (the "2.48 A replaces the
 * plot" bug). Distinct from NON_CANVAS_ARTIFACT_TYPES (which can never pin): a
 * one-off value chip OUTSIDE a flow is still allowed to pin, so the user can
 * keep a looked-up value visible next to the chat.
 */
const VALUE_CHIP_ARTIFACT_TYPES = new Set(['data_value', 'data_values'])

export function pinArtifact(artifact) {
  pinnedArtifact.value = artifact
  // A profile load (re)starts the selection flow → clear any prior manual tab
  // choice so the fullpage opens on the Plot tab for the new flow.
  if (artifact.type === 'application_profile') canvasTabUserOverridden.value = false
  // Switch canvas tab to match the pinned artifact — unless the user has
  // manually chosen a tab (then respect their choice across re-rank pins).
  if (canvasTabUserOverridden.value) return
  if (artifact.type === 'figure') canvasTab.value = 'plot'
  else if (artifact.type === 'report') canvasTab.value = 'report'
  // The guided selection flow lives on the Plot tab — there the live Ragone is
  // the MAIN view, so a profile load opens on the plot (the profile card
  // renders regardless of the tab).
  else if (artifact.type === 'application_profile') canvasTab.value = 'plot'
  // A ranked shortlist is only "a plot first" INSIDE that flow. Outside it the
  // ranking is an ordinary tool answer — it renders inline in the transcript
  // (RankedCellsInline) and the canvas does not open for it at all
  // (canvasSurfaceActive), so defaulting the tab to Plot would only mis-set the
  // tab for whatever pins next. Table is the honest default there.
  else if (artifact.type === 'ranked_cells_table')
    canvasTab.value = salesModeActive.value ? 'plot' : 'table'
  else canvasTab.value = 'table'
}

export function unpinArtifact() {
  pinnedArtifact.value = null
}

/**
 * Reconstruct the end-of-live pinned canvas artifact from a hydrated
 * message list (history reload).
 *
 * Why this exists: charts/tables live in the artifact canvas
 * (ArtifactCanvas reads `pinnedArtifact`), which is populated ONLY by
 * the live `endToolCall` auto-pin. On reopen-from-history nothing
 * replays those events, so the canvas was blank and every plot/table a
 * conversation produced vanished on reopen — even though the tool
 * outputs are present on the rehydrated messages, and even though value
 * chips (which resolve from [[V:N]] markers in the persisted text) kept
 * working. The persisted assistant text carries no `[[TOOL:id]]`
 * markers either, so there is no inline affordance to fall back on.
 *
 * We scan every tool call in chronological order and re-pin the LAST
 * renderable artifact, mirroring the live pin rule exactly: any
 * artifact_type except the intermediate `data_series` handle. Returns
 * the pinned artifact, or null when the conversation produced none.
 */
export function pinLatestArtifactFromMessages(messages) {
  let latest = null
  let latestProfile = null
  for (const m of messages || []) {
    for (const call of m.toolCalls || []) {
      if (call.isError || !call.output) continue
      const output = call.output
      const payload = output.artifact_type
        ? output
        : output.result && output.result.artifact_type
          ? output.result
          : null
      if (payload && !NON_CANVAS_ARTIFACT_TYPES.has(payload.artifact_type)) {
        latest = {
          id: newId(),
          type: payload.artifact_type,
          data: payload,
          messageId: m.id,
          sourceToolCallId: call.id,
        }
        // The persistent profile panel lives in its own signal (unlike
        // pinnedArtifact it survives a later ranked_cells_table pin), so track
        // the most recent one across the whole history.
        if (payload.artifact_type === 'application_profile') latestProfile = payload
        // Mirror live: keep the Sales-Selection breadcrumb in sync too.
        applySelectionContextFromArtifact(payload)
      }
    }
  }
  if (latest) pinArtifact(latest)
  else pinnedArtifact.value = null
  // Restore (or clear) the persistent profile panel explicitly — endToolCall
  // sets latestApplicationProfile only on the LIVE path, so without this a
  // rehydrated conversation loses the canvas rail (which is gated on this
  // signal) on reload even though the plot/table come back.
  latestApplicationProfile.value = latestProfile
  return latest
}

export function toggleReasoning(messageId) {
  const next = new Set(reasoningOpenFor.value)
  if (next.has(messageId)) next.delete(messageId)
  else next.add(messageId)
  reasoningOpenFor.value = next
}

export function isReasoningOpen(messageId) {
  return reasoningOpenFor.value.has(messageId)
}

// ───────────────────────────────────────────────────────────────────────────
// Reset / clear
// ───────────────────────────────────────────────────────────────────────────

/**
 * Drop everything bound to the CONVERSATION — id, messages, artifacts,
 * selection context — while leaving the user's unsent draft alone. Used when
 * the server tells us the stored conversation id isn't ours (404/403), where
 * throwing away what someone is typing would be gratuitous.
 */
export function discardStaleConversation() {
  // If currently streaming, abort first so no orphan handlers.
  const ac = abortController.peek()
  if (ac && !ac.signal.aborted) {
    try { ac.abort() } catch (e) { /* noop */ }
  }
  chatMessages.value = []
  messagesIdentity.value = null
  streamingMessageId.value = null
  chatStreaming.value = false
  abortController.value = null
  pinnedArtifact.value = null
  canvasTab.value = 'table'
  canvasTabUserOverridden.value = false
  reasoningOpenFor.value = new Set()
  askUserAnswered.value = new Set()
  currentConversationId.value = null
  manualPicks.value = []
  canvasFocusView.value = false
  // Also clears latestApplicationProfile + reRankPending.
  resetSelectionContext()
}

/**
 * Clear the active conversation entirely. Used when the user starts a fresh
 * chat or switches accounts (see authService.logout, which also wipes the
 * persisted 'batemo:ion:*' keys via clearIonPersistedState). Does NOT clear
 * quota (server-side state).
 */
export function clearConversation() {
  discardStaleConversation()
  draftInput.value = ''
  autoSendDraft.value = null
  // chatMinimized is derived from chatMessages, already cleared above, so it
  // reads false automatically — nothing left to assign here.
  clearUnseen()
}

/**
 * Start a fresh thread because the user ASKED to — the "BATEMO ION" nav tab and
 * an explicit "New chat". The counterpart to `isFreshArrival()`, which handles
 * the same intent when it is implied by opening the app.
 *
 * Two guards, both about not destroying work on a single click of a nav tab:
 *   - Mid-stream, it does nothing. Wiping a turn Ion is still writing would
 *     abort a request the user is paying tokens for.
 *   - An already-empty thread is left alone, so the click stays idempotent
 *     (clicking ION while on a blank Ion home must not feel like anything
 *     happened, and must not clear a draft).
 *
 * Returns whether it actually reset, so a caller can navigate either way.
 */
export function startNewConversation() {
  if (chatStreaming.peek()) return false
  const isAlreadyBlank =
    chatMessages.peek().length === 0 && currentConversationId.peek() == null
  if (isAlreadyBlank) return false
  clearConversation()
  // An explicit new chat is an arrival in every sense the user cares about, so
  // the home greets again.
  ionArrivedFresh.value = true
  return true
}

// ───────────────────────────────────────────────────────────────────────────
// Debug surface (mirrors the pattern in compareStore.js)
// ───────────────────────────────────────────────────────────────────────────

if (typeof window !== 'undefined') {
  window.__BATEMO_ION__ = {
    chatMode,
    chatOpen,
    chatMessages,
    chatStreaming,
    streamingMessageId,
    abortController,
    tokenBudget,
    insightsTokenBudget,
    weeklyTokenBudget,
    monthlyTokenBudget,
    tierProfile,
    lastUsedModel,
    chatAccess,
    currentConversationId,
    pinnedArtifact,
    latestApplicationProfile,
    reRankPending,
    canvasTab,
    reasoningOpenFor,
    chatMinimized,
    chatHasUnseen,
    chatToastVisible,
    // Mutators
    openChat,
    closeChat,
    minimizeChat,
    restoreChat,
    expandToFullpage,
    appendUserMessage,
    startAssistantMessage,
    appendDelta,
    attachSource,
    startToolCall,
    endToolCall,
    addFigure,
    addKpiGroup,
    markDone,
    markError,
    markAborted,
    updateQuota,
    pinArtifact,
    unpinArtifact,
    toggleReasoning,
    clearConversation,
    discardStaleConversation,
    clearIonPersistedState,
    ionIdentityToken,
    needsConversationRehydrate,
    applyReRankResult,
  }
}
